import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PortalNav } from "@/components/PortalNav";
import { Package, Plus, Pencil, Trash2, Loader2, FileText, ClipboardList, Users, Map, ShieldCheck, Upload, Download, AlertTriangle, LayoutDashboard, Mail, DollarSign, X, Target, Check, GripVertical, RefreshCw } from "lucide-react";

const emptyItem = { sku: "", name: "", description: "", unit_price: "", qty_on_hand: "", unit: "", category: "", low_stock_threshold: "", mfg: "", warehouse: "", bins: "", allocated: "", qty_available: "", in_return_queue: "", so_inventory: "", so_job: "", po_job: "", cost: "" };
const lineKey = () => Math.random().toString(36).slice(2);
const SALES_TABS = ["dashboard", "inventory", "quotes", "orders", "opportunities"];

export default function SalesPortal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = SALES_TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "dashboard";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (SALES_TABS.includes(t) && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const changeTab = (key) => { setTab(key); setSearchParams({ tab: key }); };

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "dashboard" && <DashboardTab onNavigate={changeTab} />}
        {tab === "inventory" && <InventoryTab />}
        {tab === "quotes" && <QuotesTab />}
        {tab === "orders" && <SalesOrdersTab />}
        {tab === "opportunities" && <OpportunitiesTab onNavigate={changeTab} />}
      </main>
    </div>
  );
}

function InventoryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item or emptyItem
  const [form, setForm] = useState(emptyItem);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/sales/inventory");
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load inventory");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyItem); setEditing("new"); };
  const openEdit = (it) => {
    setForm({ sku: it.sku || "", name: it.name || "", description: it.description || "", unit_price: it.unit_price ?? "", qty_on_hand: it.qty_on_hand ?? "", unit: it.unit || "", category: it.category || "", low_stock_threshold: it.low_stock_threshold ?? "", mfg: it.mfg || "", warehouse: it.warehouse || "", bins: it.bins || "", allocated: it.allocated ?? "", qty_available: it.qty_available ?? "", in_return_queue: it.in_return_queue ?? "", so_inventory: it.so_inventory ?? "", so_job: it.so_job ?? "", po_job: it.po_job ?? "", cost: it.cost ?? "" });
    setEditing(it.id);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = { ...form, unit_price: parseFloat(form.unit_price) || 0, qty_on_hand: parseFloat(form.qty_on_hand) || 0, low_stock_threshold: parseFloat(form.low_stock_threshold) || 0, allocated: parseFloat(form.allocated) || 0, qty_available: parseFloat(form.qty_available) || 0, in_return_queue: parseFloat(form.in_return_queue) || 0, so_inventory: parseFloat(form.so_inventory) || 0, so_job: parseFloat(form.so_job) || 0, po_job: parseFloat(form.po_job) || 0, cost: parseFloat(form.cost) || 0 };
    try {
      if (editing === "new") await api.post("/sales/inventory", payload);
      else await api.patch(`/sales/inventory/${editing}`, payload);
      toast.success("Item saved");
      setEditing(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/sales/inventory/${id}`); toast.success("Item deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed"); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/sales/inventory/import", fd);
      toast.success(`Imported ${data.created} item(s)${data.skipped?.length ? `, ${data.skipped.length} skipped` : ""}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed"); }
    finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    const headers = ["Mfg", "Item Name", "Item Description", "Item Category", "Warehouse", "Bins", "Quantity", "Allocated", "Qty Available", "In Return Queue", "SO - Inventory", "SO - Job", "PO - Job", "Customer Price", "Cost"];
    const csv = headers.map((h) => (h.includes(",") ? `"${h}"` : h)).join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inventory_master_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Package className="h-5 w-5 text-[#D4AF37]" /><h1 className="font-display font-bold text-[#0F172A] text-lg">Inventory Master</h1></div>
        <div className="flex items-center gap-2">
          <button onClick={downloadTemplate} data-testid="sales-inventory-template" className="flex items-center gap-2 border border-zinc-300 text-[#0F172A] px-4 py-2.5 text-sm hover:bg-zinc-100 transition-colors">
            <Download className="h-4 w-4" /> CSV Template
          </button>
          <label data-testid="sales-inventory-import-label" className="flex items-center gap-2 border border-zinc-300 text-[#0F172A] px-4 py-2.5 text-sm hover:bg-zinc-100 transition-colors cursor-pointer">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="sales-inventory-import-input" onChange={onImport} />
          </label>
          <button onClick={openNew} data-testid="sales-inventory-new" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors"><Plus className="h-4 w-4" /> Add Item</button>
        </div>
      </div>

      {editing && (
        <div className="bg-white border border-zinc-200 p-6 space-y-4" data-testid="sales-inventory-form">
          <h2 className="font-medium text-[#0F172A]">{editing === "new" ? "New item" : "Edit item"}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ItemField label="Mfg" value={form.mfg} onChange={set("mfg")} testid="inv-mfg" />
            <ItemField label="Item Name" value={form.name} onChange={set("name")} testid="inv-name" />
            <ItemField label="SKU" value={form.sku} onChange={set("sku")} testid="inv-sku" />
            <ItemField label="Item Category" value={form.category} onChange={set("category")} testid="inv-category" />
            <ItemField label="Warehouse" value={form.warehouse} onChange={set("warehouse")} testid="inv-warehouse" />
            <ItemField label="Bins" value={form.bins} onChange={set("bins")} testid="inv-bins" />
            <ItemField label="Quantity" type="number" value={form.qty_on_hand} onChange={set("qty_on_hand")} testid="inv-qty" />
            <ItemField label="Allocated" type="number" value={form.allocated} onChange={set("allocated")} testid="inv-allocated" />
            <ItemField label="Qty Available" type="number" value={form.qty_available} onChange={set("qty_available")} testid="inv-qty-available" />
            <ItemField label="In Return Queue" type="number" value={form.in_return_queue} onChange={set("in_return_queue")} testid="inv-return-queue" />
            <ItemField label="SO - Inventory" type="number" value={form.so_inventory} onChange={set("so_inventory")} testid="inv-so-inventory" />
            <ItemField label="SO - Job" type="number" value={form.so_job} onChange={set("so_job")} testid="inv-so-job" />
            <ItemField label="PO - Job" type="number" value={form.po_job} onChange={set("po_job")} testid="inv-po-job" />
            <ItemField label="Customer Price" type="number" value={form.unit_price} onChange={set("unit_price")} testid="inv-price" />
            <ItemField label="Cost" type="number" value={form.cost} onChange={set("cost")} testid="inv-cost" />
            <ItemField label="Unit / UoM" value={form.unit} onChange={set("unit")} testid="inv-unit" />
            <ItemField label="Low-Stock Alert At" type="number" value={form.low_stock_threshold} onChange={set("low_stock_threshold")} testid="inv-threshold" />
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Item Description</label>
              <textarea rows={2} value={form.description} onChange={set("description")} data-testid="inv-description" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent resize-none" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={saving} data-testid="sales-inventory-save" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save item"}</button>
            <button onClick={() => setEditing(null)} className="px-4 py-2.5 text-sm border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-zinc-400 text-sm" data-testid="sales-inventory-empty">No inventory items yet. Click "Add Item" to start.</div>
        ) : (
          <div className="divide-y divide-zinc-100" data-testid="sales-inventory-list">
            <div className="hidden sm:grid grid-cols-8 gap-2 px-5 py-2 text-[11px] uppercase tracking-wide text-zinc-400 bg-zinc-50">
              <span>Name</span><span>Mfg</span><span>SKU</span><span>Category</span><span>Warehouse / Bins</span><span className="text-right">Qty Avail</span><span className="text-right">Price</span><span className="text-right">Actions</span>
            </div>
            {items.map((it) => (
              <div key={it.id} className="grid grid-cols-1 sm:grid-cols-8 gap-2 px-5 py-3 items-center text-sm" data-testid={`sales-inventory-item-${it.id}`}>
                <div><div className="font-medium text-[#0F172A]">{it.name}</div>{it.unit && <div className="text-xs text-zinc-400">per {it.unit}</div>}</div>
                <div className="text-zinc-600">{it.mfg || "—"}</div>
                <div className="text-zinc-600 font-mono text-xs">{it.sku || "—"}</div>
                <div className="text-zinc-600">{it.category || "—"}</div>
                <div className="text-zinc-600 text-xs">{[it.warehouse, it.bins].filter(Boolean).join(" / ") || "—"}</div>
                <div className="sm:text-right text-zinc-800 flex sm:justify-end items-center gap-2">
                  {Number(it.low_stock_threshold) > 0 && Number(it.qty_on_hand) <= Number(it.low_stock_threshold) && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#EF4444]/10 text-[#EF4444]" data-testid={`sales-inventory-lowstock-${it.id}`}><AlertTriangle className="h-3 w-3" /> Low</span>
                  )}
                  <span>{it.qty_available ?? 0}</span>
                </div>
                <div className="sm:text-right text-zinc-800">${Number(it.unit_price || 0).toFixed(2)}</div>
                <div className="flex sm:justify-end gap-3">
                  <button onClick={() => openEdit(it)} data-testid={`sales-inventory-edit-${it.id}`} className="text-zinc-500 hover:text-[#0F172A] transition-colors"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(it.id)} data-testid={`sales-inventory-del-${it.id}`} className="text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ItemField({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">{label}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid} className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent" />
    </div>
  );
}

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const STATUSES = ["Draft", "Sent", "Accepted"];

function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [qSearch, setQSearch] = useState("");
  const [qStatus, setQStatus] = useState("");
  const openEmail = (quote) => { setEmailForm({ to: "", message: "" }); setEmailing(quote); };
  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/sales/quotes/${emailing.id}/email`, emailForm);
      toast.success(`Quote ${emailing.number} emailed to ${data.to}`);
      setEmailing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Email failed"); }
    finally { setSendingEmail(false); }
  };

  const load = async () => {
    try {
      const [qr, ir, cr] = await Promise.all([
        api.get("/sales/quotes"), api.get("/sales/inventory"), api.get("/crm/customers"),
      ]);
      setQuotes(qr.data.quotes || []); setInventory(ir.data.items || []); setCustomers(cr.data.customers || []);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setQ({ customer_id: "", lines: [], discount: 0, tax_percent: 0, status: "Draft", notes: "" }); setEditing("new"); };
  const openEdit = (quote) => { setQ({ customer_id: quote.customer_id || "", lines: (quote.lines || []).map((l) => ({ ...l, _k: lineKey() })), discount: quote.discount || 0, tax_percent: quote.tax_percent || 0, status: quote.status || "Draft", notes: quote.notes || "" }); setEditing(quote.id); };

  const addLine = () => setQ({ ...q, lines: [...q.lines, { item_id: "", name: "", sku: "", qty: 1, unit_price: 0, _k: lineKey() }] });
  const setLine = (i, patch) => setQ({ ...q, lines: q.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) });
  const pickItem = (i, itemId) => {
    const it = inventory.find((x) => x.id === itemId);
    setLine(i, it ? { item_id: it.id, name: it.name, sku: it.sku, unit_price: it.unit_price } : { item_id: "" });
  };
  const removeLine = (i) => setQ({ ...q, lines: q.lines.filter((_, idx) => idx !== i) });

  const subtotal = (q?.lines || []).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const taxable = Math.max(subtotal - (Number(q?.discount) || 0), 0);
  const tax = taxable * (Number(q?.tax_percent) || 0) / 100;
  const total = taxable + tax;

  const save = async () => {
    if (!q.customer_id) { toast.error("Please select a customer"); return; }
    if (!q.lines.length) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    const payload = { ...q, discount: Number(q.discount) || 0, tax_percent: Number(q.tax_percent) || 0, lines: q.lines.map((l) => ({ ...l, qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0 })) };
    try {
      if (editing === "new") await api.post("/sales/quotes", payload);
      else await api.patch(`/sales/quotes/${editing}`, payload);
      toast.success("Quote saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/sales/quotes/${id}`); toast.success("Quote deleted"); load(); }
    catch (err) { toast.error("Delete failed"); }
  };
  const convert = async (id) => {
    try { const { data } = await api.post(`/sales/quotes/${id}/convert`); toast.success(`Order ${data.order.number} created — inventory updated`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Convert failed"); }
  };

  if (editing) {
    return (
      <div className="bg-white border border-zinc-200 p-6 space-y-5" data-testid="sales-quote-form">
        <h2 className="font-display font-bold text-[#0F172A]">{editing === "new" ? "New Quote" : "Edit Quote"}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Customer</label>
            <select value={q.customer_id} onChange={(e) => setQ({ ...q, customer_id: e.target.value })} data-testid="quote-customer" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Status</label>
            <select value={q.status} onChange={(e) => setQ({ ...q, status: e.target.value })} data-testid="quote-status" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-2" data-testid="quote-lines">
          <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-zinc-400">
            <span className="col-span-5">Item</span><span className="col-span-2">Qty</span><span className="col-span-2">Unit Price</span><span className="col-span-2 text-right">Line Total</span><span></span>
          </div>
          {q.lines.map((l, i) => (
            <div key={l._k || i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center" data-testid={`quote-line-${i}`}>
              <select value={l.item_id || ""} onChange={(e) => pickItem(i, e.target.value)} data-testid={`quote-line-item-${i}`} className="sm:col-span-5 px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
                <option value="">Select item…</option>
                {inventory.map((it) => <option key={it.id} value={it.id}>{it.name}{it.sku ? ` (${it.sku})` : ""}</option>)}
              </select>
              <input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`quote-line-qty-${i}`} className="sm:col-span-2 px-3 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              <input type="number" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} data-testid={`quote-line-price-${i}`} className="sm:col-span-2 px-3 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              <span className="sm:col-span-2 sm:text-right text-sm text-[#0F172A]">{money((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}</span>
              <button onClick={() => removeLine(i)} data-testid={`quote-line-del-${i}`} className="sm:col-span-1 text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={addLine} data-testid="quote-add-line" className="flex items-center gap-1.5 text-sm text-[#0F172A] hover:text-[#D4AF37] transition-colors"><Plus className="h-4 w-4" /> Add line</button>
        </div>

        <div className="flex flex-col items-end gap-1 border-t border-zinc-100 pt-4 text-sm">
          <div className="flex justify-between w-56"><span className="text-zinc-500">Subtotal</span><span data-testid="quote-subtotal">{money(subtotal)}</span></div>
          <div className="flex justify-between w-56 items-center"><span className="text-zinc-500">Discount ($)</span><input type="number" value={q.discount} onChange={(e) => setQ({ ...q, discount: e.target.value })} data-testid="quote-discount" className="w-24 px-2 py-1 border border-zinc-300 text-right text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" /></div>
          <div className="flex justify-between w-56 items-center"><span className="text-zinc-500">Tax (%)</span><input type="number" value={q.tax_percent} onChange={(e) => setQ({ ...q, tax_percent: e.target.value })} data-testid="quote-tax" className="w-24 px-2 py-1 border border-zinc-300 text-right text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" /></div>
          <div className="flex justify-between w-56 font-bold text-[#0F172A] pt-1"><span>Total</span><span data-testid="quote-total">{money(total)}</span></div>
        </div>

        <div className="flex gap-3">
          <button onClick={save} disabled={saving} data-testid="sales-quote-save" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save quote"}</button>
          <button onClick={() => setEditing(null)} className="px-4 py-2.5 text-sm border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
        </div>
      </div>
    );
  }

  const qFiltered = quotes.filter((quote) => {
    if (qStatus && quote.status !== qStatus) return false;
    const t = qSearch.trim().toLowerCase();
    if (t && ![quote.number, quote.customer_name].some((v) => (v || "").toLowerCase().includes(t))) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#D4AF37]" /><h1 className="font-display font-bold text-[#0F172A] text-lg">Quotes</h1></div>
        <button onClick={openNew} data-testid="sales-quote-new" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors"><Plus className="h-4 w-4" /> New Quote</button>
      </div>
      {quotes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="quote-filters">
          <input value={qSearch} onChange={(e) => setQSearch(e.target.value)} data-testid="quote-search" placeholder="Search number or customer…" className="px-3 py-2 border border-zinc-300 bg-white text-sm w-64 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          <select value={qStatus} onChange={(e) => setQStatus(e.target.value)} data-testid="quote-filter-status" className="px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"><option value="">All statuses</option>{["Draft", "Sent", "Accepted"].map((s) => <option key={s} value={s}>{s}</option>)}</select>
        </div>
      )}
      <div className="bg-white border border-zinc-200">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
        ) : quotes.length === 0 ? (
          <div className="p-12 text-center text-zinc-400 text-sm" data-testid="sales-quotes-empty">No quotes yet. Click "New Quote" to build one.</div>
        ) : qFiltered.length === 0 ? (
          <div className="p-12 text-center text-zinc-400 text-sm" data-testid="sales-quotes-nomatch">No quotes match your search.</div>
        ) : (
          <div className="divide-y divide-zinc-100" data-testid="sales-quotes-list">
            {qFiltered.map((quote) => (
              <div key={quote.id} className="grid grid-cols-1 sm:grid-cols-5 gap-2 px-5 py-3 items-center text-sm" data-testid={`sales-quote-${quote.id}`}>
                <div className="font-mono text-[#0F172A]">{quote.number}</div>
                <div className="text-zinc-700">{quote.customer_name || "—"}</div>
                <div className="flex items-center gap-2 flex-wrap"><span className={`text-xs px-2 py-0.5 rounded-full ${quote.status === "Accepted" ? "bg-[#10B981]/10 text-[#10B981]" : quote.status === "Sent" ? "bg-[#D4AF37]/10 text-[#B8860B]" : "bg-zinc-100 text-zinc-500"}`}>{quote.status}</span>{quote.emailed_at && <span data-testid={`sales-quote-emailed-${quote.id}`} title={`Emailed to ${quote.emailed_to || ""}`} className="inline-flex items-center gap-1 text-[10px] text-[#10B981]"><Check className="h-3 w-3" /> Emailed {quote.emailed_at.slice(0, 10)}</span>}</div>
                <div className="sm:text-right font-medium text-[#0F172A]">{money(quote.total)}</div>
                <div className="flex sm:justify-end gap-3">
                  <button onClick={() => openEmail(quote)} title="Email PDF to customer" data-testid={`sales-quote-email-${quote.id}`} className="text-zinc-500 hover:text-[#10B981] transition-colors"><Mail className="h-4 w-4" /></button>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sales/quotes/${quote.id}/pdf`} target="_blank" rel="noreferrer" title="Download PDF" data-testid={`sales-quote-pdf-${quote.id}`} className="text-zinc-500 hover:text-[#D4AF37] transition-colors"><Download className="h-4 w-4" /></a>
                  <button onClick={() => convert(quote.id)} title="Convert to Sales Order" data-testid={`sales-quote-convert-${quote.id}`} className="text-zinc-500 hover:text-[#10B981] transition-colors"><ClipboardList className="h-4 w-4" /></button>
                  <button onClick={() => openEdit(quote)} data-testid={`sales-quote-edit-${quote.id}`} className="text-zinc-500 hover:text-[#0F172A] transition-colors"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(quote.id)} data-testid={`sales-quote-del-${quote.id}`} className="text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {emailing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="quote-email-modal">
          <div className="bg-white border border-zinc-200 w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <div className="flex items-center gap-2"><Mail className="h-5 w-5 text-[#D4AF37]" /><h3 className="font-display font-bold text-[#0F172A]">Email {emailing.number}</h3></div>
              <button onClick={() => setEmailing(null)} data-testid="quote-email-close"><X className="h-5 w-5 text-zinc-400 hover:text-[#0F172A]" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-zinc-500">The quote PDF for <b>{emailing.customer_name || "this customer"}</b> will be attached and emailed.</p>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Recipient email</label>
                <input type="email" value={emailForm.to} onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })} placeholder="Leave blank to use the customer on file" data-testid="quote-email-to" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Message (optional)</label>
                <textarea rows={3} value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} data-testid="quote-email-message" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none" />
              </div>
              <div className="flex gap-3">
                <button onClick={sendEmail} disabled={sendingEmail} data-testid="quote-email-send" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors disabled:opacity-60">{sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4" /> Send email</>}</button>
                <button onClick={() => setEmailing(null)} className="px-4 py-2.5 text-sm border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ORDER_STATUSES = ["Draft", "Sent", "Accepted", "Ordered"];

function SalesOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [o, setO] = useState(null);
  const [emailing, setEmailing] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [view, setView] = useState("list");
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [oSearch, setOSearch] = useState("");
  const [oStatus, setOStatus] = useState("");

  const load = async () => {
    try {
      const [or, qr, ir, cr] = await Promise.all([
        api.get("/sales/orders"), api.get("/sales/quotes"), api.get("/sales/inventory"), api.get("/crm/customers"),
      ]);
      setOrders(or.data.orders || []); setQuotes(qr.data.quotes || []);
      setInventory(ir.data.items || []); setCustomers(cr.data.customers || []);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setO({ customer_id: "", lines: [], discount: 0, tax_percent: 0, status: "Draft", notes: "" }); setEditing("new"); };
  const openEdit = (order) => { setO({ customer_id: order.customer_id || "", lines: (order.lines || []).map((l) => ({ ...l, _k: lineKey() })), discount: order.discount || 0, tax_percent: order.tax_percent || 0, status: order.status || "Draft", notes: order.notes || "" }); setEditing(order.id); };

  const prefillFromQuote = (qid) => {
    const quote = quotes.find((x) => x.id === qid);
    if (!quote) return;
    setO({ customer_id: quote.customer_id || "", lines: (quote.lines || []).map((l) => ({ item_id: l.item_id, name: l.name, sku: l.sku, qty: l.qty, unit_price: l.unit_price, _k: lineKey() })), discount: quote.discount || 0, tax_percent: quote.tax_percent || 0, status: "Draft", notes: quote.notes || "" });
  };

  const addLine = () => setO({ ...o, lines: [...o.lines, { item_id: "", name: "", sku: "", qty: 1, unit_price: 0, _k: lineKey() }] });
  const setLine = (i, patch) => setO({ ...o, lines: o.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) });
  const pickItem = (i, itemId) => {
    const it = inventory.find((x) => x.id === itemId);
    setLine(i, it ? { item_id: it.id, name: it.name, sku: it.sku, unit_price: it.unit_price } : { item_id: "" });
  };
  const removeLine = (i) => setO({ ...o, lines: o.lines.filter((_, idx) => idx !== i) });

  const subtotal = (o?.lines || []).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const taxable = Math.max(subtotal - (Number(o?.discount) || 0), 0);
  const tax = taxable * (Number(o?.tax_percent) || 0) / 100;
  const total = taxable + tax;

  const save = async () => {
    if (!o.customer_id) { toast.error("Please select a customer"); return; }
    if (!o.lines.length) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    const payload = { ...o, discount: Number(o.discount) || 0, tax_percent: Number(o.tax_percent) || 0, lines: o.lines.map((l) => ({ ...l, qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0 })) };
    try {
      if (editing === "new") { await api.post("/sales/orders", payload); toast.success("Sales order created — inventory decremented"); }
      else { await api.patch(`/sales/orders/${editing}`, payload); toast.success("Sales order updated"); }
      setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/sales/orders/${id}`); toast.success("Order deleted — inventory restored"); load(); }
    catch (err) { toast.error("Delete failed"); }
  };
  const moveOrder = async (id, status) => {
    const ord = orders.find((x) => x.id === id);
    if (!ord || ord.status === status) return;
    const prev = orders;
    setOrders((list) => list.map((x) => x.id === id ? { ...x, status } : x));
    try {
      await api.patch(`/sales/orders/${id}`, {
        customer_id: ord.customer_id || "",
        lines: (ord.lines || []).map((l) => ({ item_id: l.item_id || "", name: l.name, sku: l.sku, qty: l.qty, unit_price: l.unit_price })),
        discount: ord.discount || 0, tax_percent: ord.tax_percent || 0, status, notes: ord.notes || "",
      });
      toast.success(`Moved to ${status}`);
    } catch (err) { setOrders(prev); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not move order"); }
  };
  const openEmail = (order) => { setEmailForm({ to: "", message: "" }); setEmailing(order); };
  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/sales/orders/${emailing.id}/email`, emailForm);
      toast.success(`Order ${emailing.number} emailed to ${data.to}`);
      setEmailing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Email failed"); }
    finally { setSendingEmail(false); }
  };

  if (editing) {
    return (
      <div className="bg-white border border-zinc-200 p-6 space-y-5" data-testid="sales-order-form">
        <h2 className="font-display font-bold text-[#0F172A]">{editing === "new" ? "New Sales Order" : "Edit Sales Order"}</h2>
        {editing === "new" && quotes.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Start from an existing quote (optional)</label>
            <select onChange={(e) => e.target.value && prefillFromQuote(e.target.value)} data-testid="order-from-quote" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
              <option value="">Build manually…</option>
              {quotes.map((qt) => <option key={qt.id} value={qt.id}>{qt.number} — {qt.customer_name || "—"} ({money(qt.total)})</option>)}
            </select>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Customer</label>
            <select value={o.customer_id} onChange={(e) => setO({ ...o, customer_id: e.target.value })} data-testid="order-customer" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Status</label>
            <select value={o.status} onChange={(e) => setO({ ...o, status: e.target.value })} data-testid="order-status" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
              {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-2" data-testid="order-lines">
          <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-zinc-400">
            <span className="col-span-5">Item</span><span className="col-span-2">Qty</span><span className="col-span-2">Unit Price</span><span className="col-span-2 text-right">Line Total</span><span></span>
          </div>
          {o.lines.map((l, i) => (
            <div key={l._k || i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center" data-testid={`order-line-${i}`}>
              <select value={l.item_id || ""} onChange={(e) => pickItem(i, e.target.value)} data-testid={`order-line-item-${i}`} className="sm:col-span-5 px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
                <option value="">Select item…</option>
                {inventory.map((it) => <option key={it.id} value={it.id}>{it.name}{it.sku ? ` (${it.sku})` : ""} — {it.qty_on_hand ?? 0} on hand</option>)}
              </select>
              <input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`order-line-qty-${i}`} className="sm:col-span-2 px-3 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              <input type="number" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} data-testid={`order-line-price-${i}`} className="sm:col-span-2 px-3 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              <span className="sm:col-span-2 sm:text-right text-sm text-[#0F172A]">{money((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}</span>
              <button onClick={() => removeLine(i)} data-testid={`order-line-del-${i}`} className="sm:col-span-1 text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={addLine} data-testid="order-add-line" className="flex items-center gap-1.5 text-sm text-[#0F172A] hover:text-[#D4AF37] transition-colors"><Plus className="h-4 w-4" /> Add line</button>
        </div>

        <div className="flex flex-col items-end gap-1 border-t border-zinc-100 pt-4 text-sm">
          <div className="flex justify-between w-56"><span className="text-zinc-500">Subtotal</span><span data-testid="order-subtotal">{money(subtotal)}</span></div>
          <div className="flex justify-between w-56 items-center"><span className="text-zinc-500">Discount ($)</span><input type="number" value={o.discount} onChange={(e) => setO({ ...o, discount: e.target.value })} data-testid="order-discount" className="w-24 px-2 py-1 border border-zinc-300 text-right text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" /></div>
          <div className="flex justify-between w-56 items-center"><span className="text-zinc-500">Tax (%)</span><input type="number" value={o.tax_percent} onChange={(e) => setO({ ...o, tax_percent: e.target.value })} data-testid="order-tax" className="w-24 px-2 py-1 border border-zinc-300 text-right text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" /></div>
          <div className="flex justify-between w-56 font-bold text-[#0F172A] pt-1"><span>Total</span><span data-testid="order-total">{money(total)}</span></div>
        </div>

        {editing === "new" && <p className="text-xs text-zinc-400">Saving this order will decrement Qty on Hand in Inventory for each line item.</p>}

        <div className="flex gap-3">
          <button onClick={save} disabled={saving} data-testid="sales-order-save" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save order"}</button>
          <button onClick={() => setEditing(null)} className="px-4 py-2.5 text-sm border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
        </div>
      </div>
    );
  }

  const statusStyle = (s) => s === "Ordered" ? "bg-[#0F172A]/10 text-[#0F172A]" : s === "Accepted" ? "bg-[#10B981]/10 text-[#10B981]" : s === "Sent" ? "bg-[#D4AF37]/10 text-[#B8860B]" : "bg-zinc-100 text-zinc-500";

  const oFiltered = orders.filter((order) => {
    if (oStatus && order.status !== oStatus) return false;
    const t = oSearch.trim().toLowerCase();
    if (t && ![order.number, order.customer_name].some((v) => (v || "").toLowerCase().includes(t))) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-[#D4AF37]" /><h1 className="font-display font-bold text-[#0F172A] text-lg">Sales Orders</h1></div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-zinc-300 overflow-hidden" data-testid="order-view-toggle">
            <button onClick={() => setView("list")} data-testid="order-view-list" className={`px-3 py-2 text-sm transition-colors ${view === "list" ? "bg-[#0F172A] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>List</button>
            <button onClick={() => setView("board")} data-testid="order-view-board" className={`px-3 py-2 text-sm transition-colors ${view === "board" ? "bg-[#0F172A] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>Board</button>
          </div>
          <button onClick={openNew} data-testid="sales-order-new" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors"><Plus className="h-4 w-4" /> New Order</button>
        </div>
      </div>
      {orders.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="order-filters">
          <input value={oSearch} onChange={(e) => setOSearch(e.target.value)} data-testid="order-search" placeholder="Search number or customer…" className="px-3 py-2 border border-zinc-300 bg-white text-sm w-64 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
          <select value={oStatus} onChange={(e) => setOStatus(e.target.value)} data-testid="order-filter-status" className="px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"><option value="">All statuses</option>{ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        </div>
      )}
      {loading ? (
        <div className="bg-white border border-zinc-200 p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
      ) : orders.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-400 text-sm" data-testid="sales-orders-empty">No sales orders yet. Click "New Order" or convert a quote.</div>
      ) : oFiltered.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-400 text-sm" data-testid="sales-orders-nomatch">No orders match your search.</div>
      ) : view === "board" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="order-board">
          {ORDER_STATUSES.map((s) => (
            <div key={s}
              data-testid={`order-col-${s}`}
              onDragOver={(e) => { e.preventDefault(); setOverStage(s); }}
              onDragLeave={() => setOverStage((cur) => (cur === s ? null : cur))}
              onDrop={() => { if (dragId) moveOrder(dragId, s); setDragId(null); setOverStage(null); }}
              className={`bg-white border border-zinc-200 border-t-2 ${s === "Ordered" ? "border-t-[#0F172A]" : s === "Accepted" ? "border-t-[#10B981]" : s === "Sent" ? "border-t-[#D4AF37]" : "border-t-zinc-300"} min-h-[200px] ${overStage === s ? "ring-2 ring-[#D4AF37]" : ""}`}>
              <div className="p-3 border-b border-zinc-100 flex items-center justify-between">
                <span className="font-display font-bold text-xs text-[#0F172A]">{s}</span>
                <span className="text-[11px] font-mono text-zinc-400">{oFiltered.filter((x) => x.status === s).length} · {money(oFiltered.filter((x) => x.status === s).reduce((sum, x) => sum + (Number(x.total) || 0), 0))}</span>
              </div>
              <div className="p-3 space-y-3">
                {oFiltered.filter((x) => x.status === s).length === 0 && <p className="text-xs text-zinc-300 text-center py-6">Drop here</p>}
                {oFiltered.filter((x) => x.status === s).map((order) => (
                  <div key={order.id} draggable onDragStart={() => setDragId(order.id)} onDragEnd={() => { setDragId(null); setOverStage(null); }} data-testid={`order-card-${order.id}`} className="border border-zinc-200 bg-white p-3 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow">
                    <div className="flex items-start gap-2">
                      <GripVertical className="h-4 w-4 text-zinc-300 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <div className="font-mono text-sm text-[#0F172A] truncate">{order.number}</div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); openEmail(order); }} onMouseDown={(e) => e.stopPropagation()} draggable={false} data-testid={`order-card-email-${order.id}`} title="Email PDF to customer" className="text-zinc-300 hover:text-[#10B981] transition-colors"><Mail className="h-3.5 w-3.5" /></button>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sales/orders/${order.id}/pdf`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} draggable={false} data-testid={`order-card-pdf-${order.id}`} title="Download PDF" className="text-zinc-300 hover:text-[#D4AF37] transition-colors"><Download className="h-3.5 w-3.5" /></a>
                            <button onClick={(e) => { e.stopPropagation(); openEdit(order); }} onMouseDown={(e) => e.stopPropagation()} draggable={false} data-testid={`order-card-edit-${order.id}`} className="text-zinc-300 hover:text-[#0F172A] transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                        <div className="text-xs text-zinc-500 truncate">{order.customer_name || "—"}</div>
                        <div className="text-xs font-mono text-[#D4AF37] mt-1">{money(order.total)}</div>
                        {order.emailed_at && <div className="inline-flex items-center gap-1 text-[10px] text-[#10B981] mt-0.5"><Check className="h-3 w-3" /> Emailed</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-zinc-200">
          <div className="divide-y divide-zinc-100" data-testid="sales-orders-list">
            {oFiltered.map((order) => (
              <div key={order.id} className="grid grid-cols-1 sm:grid-cols-5 gap-2 px-5 py-3 items-center text-sm" data-testid={`sales-order-${order.id}`}>
                <div className="font-mono text-[#0F172A]">{order.number}</div>
                <div className="text-zinc-700">{order.customer_name || "—"}</div>
                <div className="flex items-center gap-2 flex-wrap"><span className={`text-xs px-2 py-0.5 rounded-full ${statusStyle(order.status)}`}>{order.status}</span>{order.emailed_at && <span data-testid={`sales-order-emailed-${order.id}`} title={`Emailed to ${order.emailed_to || ""}`} className="inline-flex items-center gap-1 text-[10px] text-[#10B981]"><Check className="h-3 w-3" /> Emailed {order.emailed_at.slice(0, 10)}</span>}</div>
                <div className="sm:text-right font-medium text-[#0F172A]">{money(order.total)}</div>
                <div className="flex sm:justify-end gap-3">
                  <button onClick={() => openEmail(order)} title="Email PDF to customer" data-testid={`sales-order-email-${order.id}`} className="text-zinc-500 hover:text-[#10B981] transition-colors"><Mail className="h-4 w-4" /></button>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/sales/orders/${order.id}/pdf`} target="_blank" rel="noreferrer" title="Download PDF" data-testid={`sales-order-pdf-${order.id}`} className="text-zinc-500 hover:text-[#D4AF37] transition-colors"><Download className="h-4 w-4" /></a>
                  <button onClick={() => openEdit(order)} data-testid={`sales-order-edit-${order.id}`} className="text-zinc-500 hover:text-[#0F172A] transition-colors"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(order.id)} data-testid={`sales-order-del-${order.id}`} className="text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {emailing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="order-email-modal">
          <div className="bg-white border border-zinc-200 w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <div className="flex items-center gap-2"><Mail className="h-5 w-5 text-[#D4AF37]" /><h3 className="font-display font-bold text-[#0F172A]">Email {emailing.number}</h3></div>
              <button onClick={() => setEmailing(null)} data-testid="order-email-close"><X className="h-5 w-5 text-zinc-400 hover:text-[#0F172A]" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-zinc-500">The order PDF for <b>{emailing.customer_name || "this customer"}</b> will be attached and emailed.</p>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Recipient email</label>
                <input type="email" value={emailForm.to} onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })} placeholder="Leave blank to use the customer on file" data-testid="order-email-to" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Message (optional)</label>
                <textarea rows={3} value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} data-testid="order-email-message" className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none" />
              </div>
              <div className="flex gap-3">
                <button onClick={sendEmail} disabled={sendingEmail} data-testid="order-email-send" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors disabled:opacity-60">{sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4" /> Send email</>}</button>
                <button onClick={() => setEmailing(null)} className="px-4 py-2.5 text-sm border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DashboardTab({ onNavigate }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const load = async () => {
    setRefreshing(true);
    try { const { data } = await api.get("/sales/dashboard"); setD(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>;
  if (!d) return null;

  const cards = [
    { key: "quotes", label: "Open Quotes", value: d.open_quotes, sub: `${money(d.open_quotes_value)} value`, tab: "quotes", Icon: FileText },
    { key: "orders", label: "Pipeline Value", value: money(d.pipeline_total), sub: `${d.orders_total} order${d.orders_total === 1 ? "" : "s"}`, tab: "orders", Icon: DollarSign },
    { key: "inventory", label: "Inventory Items", value: d.inventory_count, sub: "in catalog", tab: "inventory", Icon: Package },
    { key: "lowstock", label: "Low Stock", value: d.low_stock_count, sub: "need reorder", tab: "inventory", Icon: AlertTriangle, alert: d.low_stock_count > 0 },
  ];

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><LayoutDashboard className="h-5 w-5 text-[#D4AF37]" /><h1 className="font-display font-bold text-[#0F172A] text-lg">Overview</h1></div>
        <button onClick={load} disabled={refreshing} data-testid="dash-refresh" className="flex items-center gap-2 border border-zinc-300 text-[#0F172A] px-3 py-2 text-sm hover:bg-zinc-100 transition-colors disabled:opacity-60"><RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button key={c.key} onClick={() => onNavigate(c.tab)} data-testid={`sales-dash-card-${c.key}`}
            className={`text-left bg-white border p-5 hover:shadow-md transition-shadow ${c.alert ? "border-[#EF4444]/40" : "border-zinc-200"}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-zinc-400">{c.label}</span>
              <c.Icon className={`h-4 w-4 ${c.alert ? "text-[#EF4444]" : "text-[#D4AF37]"}`} />
            </div>
            <div className={`mt-2 text-2xl font-display font-bold ${c.alert ? "text-[#EF4444]" : "text-[#0F172A]"}`}>{c.value}</div>
            <div className="text-xs text-zinc-400 mt-1">{c.sub}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 p-5" data-testid="sales-dash-orders-status">
          <h2 className="font-medium text-[#0F172A] mb-4">Sales Orders by Status</h2>
          <div className="space-y-3">
            {Object.entries(d.orders_by_status).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between text-sm">
                <span className="text-zinc-500">{status}</span>
                <span className="font-medium text-[#0F172A]">{count}</span>
              </div>
            ))}
            {d.orders_total === 0 && <p className="text-sm text-zinc-400">No sales orders yet.</p>}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-5" data-testid="sales-dash-lowstock">
          <h2 className="font-medium text-[#0F172A] mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[#EF4444]" /> Low-Stock Items</h2>
          {d.low_stock_items.length === 0 ? (
            <p className="text-sm text-zinc-400">All items are above their reorder threshold.</p>
          ) : (
            <div className="divide-y divide-zinc-100">
              {d.low_stock_items.map((i) => (
                <div key={i.id} className="flex items-center justify-between py-2 text-sm" data-testid={`sales-dash-lowstock-item-${i.id}`}>
                  <div><span className="text-[#0F172A]">{i.name}</span>{i.sku && <span className="text-zinc-400 font-mono text-xs ml-2">{i.sku}</span>}</div>
                  <span className="text-[#EF4444] font-medium">{i.qty_on_hand} / {i.low_stock_threshold}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-5" data-testid="sales-dash-opportunities">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium text-[#0F172A] flex items-center gap-2"><Target className="h-4 w-4 text-[#D4AF37]" /> Opportunity Pipeline</h2>
          <button onClick={() => onNavigate("opportunities")} data-testid="sales-dash-opps-link" className="text-xs text-zinc-400 hover:text-[#0F172A] transition-colors">View all →</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div><div className="text-xs uppercase tracking-wide text-zinc-400">Open Pipeline</div><div className="mt-1 text-xl font-display font-bold text-[#0F172A]" data-testid="dash-opp-pipeline">{money(d.opp_open_pipeline || 0)}</div></div>
          <div><div className="text-xs uppercase tracking-wide text-zinc-400">Win Rate</div><div className="mt-1 text-xl font-display font-bold text-[#10B981]" data-testid="dash-opp-winrate">{d.opp_win_rate || 0}%</div></div>
          <div><div className="text-xs uppercase tracking-wide text-zinc-400">Total Opps</div><div className="mt-1 text-xl font-display font-bold text-[#0F172A]" data-testid="dash-opp-total">{d.opportunities_total || 0}</div></div>
          <div><div className="text-xs uppercase tracking-wide text-zinc-400">Won</div><div className="mt-1 text-xl font-display font-bold text-[#0F172A]">{(d.opp_by_stage || {}).Won || 0}</div></div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm border-t border-zinc-100 pt-3" data-testid="dash-opp-by-stage">
          {Object.entries(d.opp_by_stage || {}).map(([stage, count]) => (
            <div key={stage} className="flex items-center gap-2"><span className="text-zinc-500">{stage}</span><span className="font-medium text-[#0F172A]">{count}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

const OPP_STAGES = ["Abandoned", "Investigation/Data Gathering", "Lost", "Proposal/Negotiation", "Won"];
const OPP_LEAD_SOURCES = ["Cold-Call", "Existing Customer", "Referral", "Service Technician", "Website"];
const OPP_TYPES = ["New Equipment", "Rental Agreement", "Resale/Dealer", "Service Agreement", "Service Repair"];
const OPP_PROPOSAL_TYPES = ["Contract", "Job", "Rental"];
const OPP_FORECAST = ["1-30 Day Close", "31-45 Day Close", "60 Day Close", "Budget"];
const oppSel = "w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]";
const emptyOpp = { name: "", customer_id: "", stage: "Investigation/Data Gathering", lead_source: "", type: "", proposal_type: "", forecast_category: "", amount: "", close_date: "", created_by_rep_id: "", owner_rep_id: "", job_notes: "" };

function OppField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function OpportunitiesTab({ onNavigate }) {
  const [opps, setOpps] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(emptyOpp);
  const [current, setCurrent] = useState(null);
  const [view, setView] = useState("list");
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [search, setSearch] = useState("");
  const [fStage, setFStage] = useState("");
  const [fOwner, setFOwner] = useState("");
  const [fType, setFType] = useState("");
  const [sortBy, setSortBy] = useState("");

  const load = async () => {
    try {
      const [or, cr, rr] = await Promise.all([
        api.get("/sales/opportunities"), api.get("/crm/customers"), api.get("/crm/reps"),
      ]);
      setOpps(or.data.opportunities || []); setCustomers(cr.data.customers || []); setReps(rr.data.reps || []);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const openNew = () => { setF(emptyOpp); setCurrent(null); setEditing("new"); };
  const openEdit = (o) => {
    setF({ name: o.name || "", customer_id: o.customer_id || "", stage: o.stage || "", lead_source: o.lead_source || "", type: o.type || "", proposal_type: o.proposal_type || "", forecast_category: o.forecast_category || "", amount: o.amount ?? "", close_date: o.close_date || "", created_by_rep_id: o.created_by_rep_id || "", owner_rep_id: o.owner_rep_id || "", job_notes: o.job_notes || "" });
    setCurrent(o); setEditing(o.id);
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Opportunity name is required"); return; }
    setSaving(true);
    const payload = { ...f, amount: parseFloat(f.amount) || 0 };
    try {
      if (editing === "new") { await api.post("/sales/opportunities", payload); toast.success("Opportunity created"); }
      else { await api.patch(`/sales/opportunities/${editing}`, payload); toast.success("Opportunity updated"); }
      setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/sales/opportunities/${id}`); toast.success("Opportunity deleted"); load(); }
    catch (err) { toast.error("Delete failed"); }
  };
  const move = async (id, stage) => {
    const o = opps.find((x) => x.id === id);
    if (!o || o.stage === stage) return;
    const prev = opps;
    setOpps((list) => list.map((x) => x.id === id ? { ...x, stage } : x));
    try {
      await api.patch(`/sales/opportunities/${id}`, {
        name: o.name, customer_id: o.customer_id || "", stage,
        lead_source: o.lead_source || "", type: o.type || "", proposal_type: o.proposal_type || "",
        forecast_category: o.forecast_category || "", amount: Number(o.amount) || 0, close_date: o.close_date || "",
        created_by_rep_id: o.created_by_rep_id || "", owner_rep_id: o.owner_rep_id || "", job_notes: o.job_notes || "",
      });
      toast.success(`Moved to ${stage}`);
    } catch (err) { setOpps(prev); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not move opportunity"); }
  };
  const createQuote = async (o) => {
    try {
      await api.post("/sales/quotes", {
        customer_id: o.customer_id || "", discount: 0, tax_percent: 0, status: "Draft",
        notes: `From opportunity: ${o.name}`,
        lines: [{ item_id: "", name: o.name || "Opportunity", sku: "", qty: 1, unit_price: Number(o.amount) || 0 }],
      });
      toast.success("Quote created from opportunity");
      if (onNavigate) onNavigate("quotes");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create quote"); }
  };
  const createOrder = async (o) => {
    try {
      await api.post("/sales/orders", {
        customer_id: o.customer_id || "", discount: 0, tax_percent: 0, status: "Draft",
        notes: `From opportunity: ${o.name}`,
        lines: [{ item_id: "", name: o.name || "Opportunity", sku: "", qty: 1, unit_price: Number(o.amount) || 0 }],
      });
      toast.success("Sales order created from opportunity");
      if (onNavigate) onNavigate("orders");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create order"); }
  };

  if (editing) {
    return (
      <div className="bg-white border border-zinc-200 p-6 space-y-5" data-testid="opp-form">
        <h2 className="font-display font-bold text-[#0F172A]">{editing === "new" ? "New Opportunity" : "Edit Opportunity"}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <OppField label="Opportunity Name">
            <input value={f.name} onChange={set("name")} data-testid="opp-name" className={oppSel} />
          </OppField>
          <OppField label="Customer">
            <select value={f.customer_id} onChange={set("customer_id")} data-testid="opp-customer" className={oppSel}>
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </OppField>
          <OppField label="Stage">
            <select value={f.stage} onChange={set("stage")} data-testid="opp-stage" className={oppSel}>
              <option value="">Select…</option>
              {OPP_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </OppField>
          <OppField label="Lead Source">
            <select value={f.lead_source} onChange={set("lead_source")} data-testid="opp-lead-source" className={oppSel}>
              <option value="">Select…</option>
              {OPP_LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </OppField>
          <OppField label="Type">
            <select value={f.type} onChange={set("type")} data-testid="opp-type" className={oppSel}>
              <option value="">Select…</option>
              {OPP_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </OppField>
          <OppField label="Proposal Type">
            <select value={f.proposal_type} onChange={set("proposal_type")} data-testid="opp-proposal-type" className={oppSel}>
              <option value="">Select…</option>
              {OPP_PROPOSAL_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </OppField>
          <OppField label="Forecast Category">
            <select value={f.forecast_category} onChange={set("forecast_category")} data-testid="opp-forecast" className={oppSel}>
              <option value="">Select…</option>
              {OPP_FORECAST.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </OppField>
          <OppField label="Amount">
            <input type="number" value={f.amount} onChange={set("amount")} data-testid="opp-amount" className={oppSel} />
          </OppField>
          <OppField label="Close Date">
            <input type="date" value={f.close_date} onChange={set("close_date")} data-testid="opp-close-date" className={oppSel} />
          </OppField>
          <OppField label="Created By (Rep)">
            <select value={f.created_by_rep_id} onChange={set("created_by_rep_id")} data-testid="opp-created-by" className={oppSel}>
              <option value="">Select rep…</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </OppField>
          <OppField label="Owner (Rep)">
            <select value={f.owner_rep_id} onChange={set("owner_rep_id")} data-testid="opp-owner" className={oppSel}>
              <option value="">Select rep…</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </OppField>
        </div>
        <OppField label="Job (Notes)">
          <textarea rows={3} value={f.job_notes} onChange={set("job_notes")} data-testid="opp-notes" className={`${oppSel} resize-none`} />
        </OppField>
        {current && (
          <div className="flex flex-wrap gap-x-8 gap-y-1 text-xs text-zinc-400 border-t border-zinc-100 pt-3">
            <span>Created Date: <span className="text-zinc-600">{(current.created_at || "").slice(0, 10) || "—"}</span></span>
            <span>Last Modified: <span className="text-zinc-600">{(current.updated_at || "").slice(0, 10) || "—"}</span></span>
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={save} disabled={saving} data-testid="opp-save" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save opportunity"}</button>
          <button onClick={() => setEditing(null)} className="px-4 py-2.5 text-sm border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
        </div>
      </div>
    );
  }

  const stageStyle = (s) => s === "Won" ? "bg-[#10B981]/10 text-[#10B981]" : (s === "Lost" || s === "Abandoned") ? "bg-[#EF4444]/10 text-[#EF4444]" : s === "Proposal/Negotiation" ? "bg-[#D4AF37]/10 text-[#B8860B]" : "bg-zinc-100 text-zinc-500";
  const visible = opps.filter((o) => {
    if (fStage && o.stage !== fStage) return false;
    if (fOwner && o.owner_rep_id !== fOwner) return false;
    if (fType && o.type !== fType) return false;
    const t = search.trim().toLowerCase();
    if (t && ![o.name, o.customer_name, o.owner_rep_name].some((v) => (v || "").toLowerCase().includes(t))) return false;
    return true;
  });
  const sorted = [...visible].sort((a, b) => {
    if (sortBy === "amount") return (Number(b.amount) || 0) - (Number(a.amount) || 0);
    if (sortBy === "close_date") return (a.close_date || "9999-99-99").localeCompare(b.close_date || "9999-99-99");
    if (sortBy === "stage") return OPP_STAGES.indexOf(a.stage) - OPP_STAGES.indexOf(b.stage);
    return 0;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><Target className="h-5 w-5 text-[#D4AF37]" /><h1 className="font-display font-bold text-[#0F172A] text-lg">Opportunities</h1></div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-zinc-300 overflow-hidden" data-testid="opp-view-toggle">
            <button onClick={() => setView("list")} data-testid="opp-view-list" className={`px-3 py-2 text-sm transition-colors ${view === "list" ? "bg-[#0F172A] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>List</button>
            <button onClick={() => setView("board")} data-testid="opp-view-board" className={`px-3 py-2 text-sm transition-colors ${view === "board" ? "bg-[#0F172A] text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>Board</button>
          </div>
          <button onClick={openNew} data-testid="opp-new" className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm hover:bg-[#1E293B] transition-colors"><Plus className="h-4 w-4" /> New Opportunity</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="opp-filters">
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="opp-search" placeholder="Search name, customer, owner…" className="px-3 py-2 border border-zinc-300 bg-white text-sm w-64 outline-none focus:ring-2 focus:ring-[#D4AF37]" />
        <select value={fStage} onChange={(e) => setFStage(e.target.value)} data-testid="opp-filter-stage" className="px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"><option value="">All stages</option>{OPP_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="opp-filter-type" className="px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"><option value="">All types</option>{OPP_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <select value={fOwner} onChange={(e) => setFOwner(e.target.value)} data-testid="opp-filter-owner" className="px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"><option value="">All owners</option>{reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="opp-sort" className="px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"><option value="">Sort: default</option><option value="amount">Amount (high→low)</option><option value="close_date">Close date</option><option value="stage">Stage</option></select>
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
      ) : opps.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-400 text-sm" data-testid="opps-empty">No opportunities yet. Click "New Opportunity" to add one.</div>
      ) : view === "board" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="opp-board">
          {OPP_STAGES.map((s) => (
            <div key={s}
              data-testid={`opp-col-${s}`}
              onDragOver={(e) => { e.preventDefault(); setOverStage(s); }}
              onDragLeave={() => setOverStage((cur) => (cur === s ? null : cur))}
              onDrop={() => { if (dragId) move(dragId, s); setDragId(null); setOverStage(null); }}
              className={`bg-white border border-zinc-200 border-t-2 ${s === "Won" ? "border-t-[#10B981]" : (s === "Lost" || s === "Abandoned") ? "border-t-[#EF4444]" : "border-t-[#D4AF37]"} min-h-[200px] ${overStage === s ? "ring-2 ring-[#D4AF37]" : ""}`}>
              <div className="p-3 border-b border-zinc-100 flex items-center justify-between">
                <span className="font-display font-bold text-xs text-[#0F172A]">{s}</span>
                <span className="text-[11px] font-mono text-zinc-400">{sorted.filter((o) => o.stage === s).length} · {money(sorted.filter((o) => o.stage === s).reduce((sum, o) => sum + (Number(o.amount) || 0), 0))}</span>
              </div>
              <div className="p-3 space-y-3">
                {sorted.filter((o) => o.stage === s).length === 0 && <p className="text-xs text-zinc-300 text-center py-6">Drop here</p>}
                {sorted.filter((o) => o.stage === s).map((o) => (
                  <div key={o.id} draggable onDragStart={() => setDragId(o.id)} onDragEnd={() => { setDragId(null); setOverStage(null); }} data-testid={`opp-card-${o.id}`} className="border border-zinc-200 bg-white p-3 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow">
                    <div className="flex items-start gap-2">
                      <GripVertical className="h-4 w-4 text-zinc-300 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <div className="font-medium text-sm text-[#0F172A] truncate">{o.name}</div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {o.stage === "Won" && <button onClick={(e) => { e.stopPropagation(); createOrder(o); }} onMouseDown={(e) => e.stopPropagation()} draggable={false} data-testid={`opp-card-order-${o.id}`} title="Create sales order" className="text-zinc-300 hover:text-[#10B981] transition-colors"><ClipboardList className="h-3.5 w-3.5" /></button>}
                            <button onClick={(e) => { e.stopPropagation(); createQuote(o); }} onMouseDown={(e) => e.stopPropagation()} draggable={false} data-testid={`opp-card-quote-${o.id}`} title="Create quote" className="text-zinc-300 hover:text-[#D4AF37] transition-colors"><FileText className="h-3.5 w-3.5" /></button>
                            <button onClick={(e) => { e.stopPropagation(); openEdit(o); }} onMouseDown={(e) => e.stopPropagation()} draggable={false} data-testid={`opp-card-edit-${o.id}`} className="text-zinc-300 hover:text-[#0F172A] transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                        <div className="text-xs text-zinc-500 truncate">{o.customer_name || "—"}</div>
                        {o.amount != null && <div className="text-xs font-mono text-[#D4AF37] mt-1">{money(o.amount)}</div>}
                        <div className="text-[11px] text-zinc-400 mt-0.5">{o.owner_rep_name || "Unassigned"}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-zinc-200">
          <div className="divide-y divide-zinc-100" data-testid="opps-list">
            <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-2.5 text-[11px] uppercase tracking-wide text-zinc-400">
              <span className="col-span-3">Opportunity</span><span className="col-span-2">Customer</span><span className="col-span-2">Stage</span><span className="col-span-2 text-right">Amount</span><span className="col-span-2">Close / Owner</span><span></span>
            </div>
            {sorted.map((o) => (
              <div key={o.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 px-5 py-3 items-center text-sm" data-testid={`opp-${o.id}`}>
                <div className="sm:col-span-3 font-medium text-[#0F172A]">{o.name}</div>
                <div className="sm:col-span-2 text-zinc-700">{o.customer_name || "—"}</div>
                <div className="sm:col-span-2"><span className={`text-xs px-2 py-0.5 rounded-full ${stageStyle(o.stage)}`}>{o.stage || "—"}</span></div>
                <div className="sm:col-span-2 sm:text-right font-medium text-[#0F172A]">{money(o.amount)}</div>
                <div className="sm:col-span-2 text-xs text-zinc-500"><div>{o.close_date || "—"}</div><div>{o.owner_rep_name || "Unassigned"}</div></div>
                <div className="sm:col-span-1 flex sm:justify-end gap-3">
                  <button onClick={() => createQuote(o)} title="Create quote from this opportunity" data-testid={`opp-quote-${o.id}`} className="text-zinc-500 hover:text-[#D4AF37] transition-colors"><FileText className="h-4 w-4" /></button>
                  {o.stage === "Won" && <button onClick={() => createOrder(o)} title="Create sales order" data-testid={`opp-order-${o.id}`} className="text-zinc-500 hover:text-[#10B981] transition-colors"><ClipboardList className="h-4 w-4" /></button>}
                  <button onClick={() => openEdit(o)} data-testid={`opp-edit-${o.id}`} className="text-zinc-500 hover:text-[#0F172A] transition-colors"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(o.id)} data-testid={`opp-del-${o.id}`} className="text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
